import React, { useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { FileUploader } from "react-drag-drop-files";
import { convertGridToMatch } from "@shared/modals/ImportGameModal/grid/gridGameImporter";
import { GridEvent } from "@shared/modals/ImportGameModal/grid/gridTypes";
import { playbookState, resetMapState } from "@shared/states/frameState";
import { closeAllModals, matchState } from "@shared/states/uiStates";
import { StaticDataContext } from "@shared/context/StaticDataContext";
import { trackEvent } from "@shared/utils";
import { useApi } from "@shared/api/hooks";
import { saveNewCustomGame } from "@shared/api/custom_game";

const fileTypes = ["JSONL"];

interface ImportGridProps {}

const ImportGrid: React.FC<ImportGridProps> = (props) => {
	const [isImporting, setIsImporting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { champions } = useContext(StaticDataContext);
	const { request: requestSaveNewCustomGame } = useApi(saveNewCustomGame);

	useEffect(() => {
		matchState.loading = isImporting;
		return () => {
			matchState.loading = false;
		};
	}, [isImporting]);

	const handleChange = (file: File | File[]) => {
		if (Array.isArray(file)) file = file[0];

		const reader = new FileReader();
		reader.readAsText(file, "UTF-8");

		reader.onload = function (evt) {
			setIsImporting(true);
			setTimeout(async () => {
				try {
					const res = evt.target?.result as string | null;
					const lines = res
						?.split(/\n/)
						.filter((l) => l.length > 0)
						.map((l) => JSON.parse(l));
					const match = convertGridToMatch(lines as GridEvent[], champions);

					if (!match) {
						setIsImporting(false);
						setError("Could not parse the game file.");
						return;
					}

					trackEvent("import_grid");
					const saved_game = await requestSaveNewCustomGame({
						game_id: match.summary.gameName!,
						game_provider: "grid",
						match,
					});
					if (!saved_game) {
						setIsImporting(false);
						setError("Failed to save the game. Please try again.");
						return;
					}
					setIsImporting(false);
					resetMapState();
					matchState.gameProvider = "grid";
					matchState.customGameId = saved_game.custom_game.id;
					matchState.match = match;
					playbookState.customGameId = saved_game.custom_game.id;
					playbookState.customMatchToken = saved_game.token;
					closeAllModals();
				} catch (e) {
					setIsImporting(false);
					setError("Something went wrong importing the file. Please try again.");
					console.error("Grid import failed:", e);
					Sentry.captureException(e);
				}
			}, 10);
		};
		reader.onerror = function (evt) {
			setIsImporting(false);
			setError("Could not read the selected file.");
			console.error(evt);
		};
	};

	return (
		<div>
			{error && <div className={"text-red-500"}>{error}</div>}
			{isImporting ? (
				<div className={"text-slate-400"}>Importing...</div>
			) : (
				<FileUploader
					handleChange={handleChange}
					name="file"
					types={fileTypes}
					classes={"!border-slate-500 !text-white"}
				>
					<div
						className={
							"border border-slate-400 border-dashed rounded p-2 text-sm cursor-pointer"
						}
					>
						Click or Drag{" "}
						<span
							className={
								"rounded bg-blue-400 bg-opacity-40 px-1 border border-blue-400"
							}
						>
							.jsonl
						</span>{" "}
						Game file here
					</div>
				</FileUploader>
			)}
		</div>
	);
};

export { ImportGrid };
